import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { Button } from "../../Button";
import PermissionsCheckboxGroup from "../PermissionsCheckboxGroup";
import { AdminPermission } from "../../../api/generated/models/AdminPermission";
import { AdminAccountViewModel } from "../../../api/generated/models/AdminAccountViewModel";
import { AdminAccountsService } from "../../../api/generated/services/AdminAccountsService";
import { useAuth } from "../../../context/AuthContext";
import { ADMIN_NAV, SUPER_ADMIN_LABEL } from "../../../config/adminNav";
import {
  collectPermissionGroups,
  editBlockReason,
  grantBlockReason,
  labelPermissions,
  listPermissionOptions,
} from "../../../helpers/adminPermissions";
import { ReactComponent as CloseIcon } from "../../../assets/icons/x.svg";

const PERMISSION_OPTIONS = listPermissionOptions(
  collectPermissionGroups(ADMIN_NAV),
  SUPER_ADMIN_LABEL
);

interface UpdatePermissionsModalProps {
  isOpen: boolean;
  admin: AdminAccountViewModel | null;
  onClose: () => void;
  onSuccess: () => void;
}

const UpdatePermissionsModal: React.FC<UpdatePermissionsModalProps> = ({
  isOpen,
  admin,
  onClose,
  onSuccess,
}) => {
  const { user } = useAuth();
  const [selectedPermissions, setSelectedPermissions] = useState<
    AdminPermission[]
  >([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const grantorPermissions = user?.adminPermissions ?? [];

  // Cases the backend refuses outright: the form is shown read-only.
  const lockedReason =
    admin && user
      ? editBlockReason(
          { id: admin.id, permissions: admin.adminPermissions ?? [] },
          { id: user.id, permissions: grantorPermissions }
        )
      : null;

  useEffect(() => {
    if (admin && admin.adminPermissions) {
      // Every known permission has a checkbox, so nothing is sent back unseen.
      // A name the dashboard does not know (one removed from the backend) is
      // dropped here, and so removed on save.
      const permissions = admin.adminPermissions
        .map((p) => p as AdminPermission)
        .filter((p) => Object.values(AdminPermission).includes(p));
      setSelectedPermissions(permissions);
    }
  }, [admin]);

  const handleClose = () => {
    if (!loading) {
      setError(null);
      onClose();
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (lockedReason) {
      setError(lockedReason);
      return;
    }

    if (!admin?.id) {
      setError("Admin ID is missing");
      return;
    }

    if (selectedPermissions.length === 0) {
      setError("Please select at least one permission");
      return;
    }

    // The backend checks every permission sent, including ones the admin
    // already held, so those have to be removed before saving.
    const ungrantable = selectedPermissions.filter(
      (permission) => grantBlockReason(permission, grantorPermissions) !== null
    );
    if (ungrantable.length > 0) {
      const names = labelPermissions(ungrantable, PERMISSION_OPTIONS)
        .map(({ label }) => label)
        .join(", ");
      setError(
        `You can only grant permissions you have yourself. Untick ${names} to save.`
      );
      return;
    }

    setLoading(true);

    try {
      await AdminAccountsService.postApiAdminAccountsUpdateAdminAccountPermissions(
        {
          adminAccountId: admin.id,
          permissions: selectedPermissions,
        }
      );
      handleClose();
      onSuccess();
    } catch (err: any) {
      console.error("Error updating admin permissions:", err);
      setError(
        err?.body?.message ||
          err?.message ||
          "Failed to update permissions. Please try again."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!admin) {
    return null;
  }

  return (
    <Modal
      className="modal-content md:max-w-[600px]"
      overlayClassName="modal-overlay"
      isOpen={isOpen}
      onRequestClose={handleClose}
      contentLabel="Update Permissions"
    >
      <div className="flex justify-between mb-6">
        <h2 className="text-primary font-medium text-xl">Update Permissions</h2>
        <Button onClick={handleClose} variant="icon" disabled={loading}>
          <CloseIcon className="w-3 h-3" />
        </Button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Email Address
          </label>
          <div className="p-3 bg-gray-50 border border-gray-300 rounded-lg text-gray-700">
            {admin.email}
          </div>
        </div>

        <PermissionsCheckboxGroup
          selectedPermissions={selectedPermissions}
          onChange={setSelectedPermissions}
          grantorPermissions={grantorPermissions}
          disabled={loading}
          lockedReason={lockedReason}
        />

        {error && (
          <div className="text-red-500 text-sm bg-red-50 border border-red-200 p-3 rounded-lg">
            {error}
          </div>
        )}

        <div className="flex gap-3 justify-end">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            Cancel
          </Button>
          <Button type="submit" isLoading={loading} disabled={Boolean(lockedReason)}>
            Update Permissions
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default UpdatePermissionsModal;
